import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL, AUTH_HEADER_NAME, devLog, URL_ALLOWED_LIST } from './jwt.constant';
import { MijnJwtService } from './mijn-jwt.service';

/**
 * Attaches the Auth0 Bearer token to outgoing HTTP requests matching `URL_ALLOWED_LIST`.
 *
 * Registered automatically by `provideMijnAuthSessieManager()` and `provideMijnJwtInterceptor()`.
 * Do not register this interceptor manually in production code — use the provider functions instead.
 *
 * The export exists solely for unit test setup: provide it explicitly via `HTTP_INTERCEPTORS`
 * in your test bed when you need to test HTTP behaviour involving the interceptor.
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private readonly headerName = inject(AUTH_HEADER_NAME);
    private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true });
    private readonly jwtService = inject(MijnJwtService);
    private allowedList: string[] = URL_ALLOWED_LIST;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isAuthHeaderNeeded(req.url)) {
            return next.handle(req);
        }

        const token = this.jwtService.getTokenForInterceptor();
        devLog('[JwtInterceptor] intercept', req.url, 'token:', token ? 'present' : 'null');

        if (!token) {
            return next.handle(req);
        }

        const rewrittenUrl = this.rewriteUrl(req.url);
        return next.handle(
            req.clone({
                url: rewrittenUrl,
                headers: req.headers.set(this.headerName, `Bearer ${token}`)
            })
        );
    }

    private rewriteUrl(url: string): string {
        if (!this.apiBaseUrl || this.isLocalhost() || url.startsWith('http')) {
            return url;
        }

        const base = this.apiBaseUrl.replace(/\/$/, '');
        return `${base}${url}`;
    }

    private isLocalhost(): boolean {
        return typeof window !== 'undefined' && window.location.hostname === 'localhost';
    }

    private readonly excludedList: string[] = ['/services/apic-sessiebeheer/'];

    private isAuthHeaderNeeded(url: string): boolean {
        if (this.excludedList.some((excluded) => url.includes(excluded))) {
            return false;
        }
        return this.allowedList.some((pattern) => {
            const regex = new RegExp(`^${pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')}`);
            return regex.test(url);
        });
    }
}
